import { memo } from 'react';
import { cn } from '@/lib/utils';
import { rowDescriptor } from '../../lib/kindMap';
import { phtFullDate, phtTimeOfDay } from '../../lib/phtDay';
import { ActivitySeverity, type ActivityLogDto } from '../../types';
import { EvidenceBlock } from './evidence';

interface ActivityEventRowProps {
  entry: ActivityLogDto;
  /** False on the last row of the last day group, so the connector does not dangle into the footer. */
  showConnector: boolean;
}

/**
 * One feed row: kind tile, actor + predicate, whatever evidence the event carries, timestamp + origin.
 *
 * ## Two things here are load-bearing and invisible
 *
 * **The sentence is never assembled from parts on the client.** `actorSnapshot` is a point-in-time snapshot
 * and `summary` is a predicate written by the server's projection map, so the row prints
 * `{actor} {predicate}` and nothing else. Restating a summary here would put feed copy in two places, and
 * the server's copy is the one under test.
 *
 * **The timestamp is a PHT wall clock, not a relative string.** The day is already established by the sticky
 * header above, which groups on a fixed +08:00 boundary — and the repo's `formatRelativeTime` computes
 * "Yesterday" from the BROWSER's calendar. Mixing the two puts a row reading "Yesterday" under a header
 * reading "Today" for anyone outside PHT. So this column shows the time and only the time — and its hover
 * gives the complete PHT date beside it, for a row read after its header has scrolled away.
 */
export const ActivityEventRow = memo(function ActivityEventRow({
  entry,
  showConnector,
}: ActivityEventRowProps) {
  const { icon: Icon, tile } = rowDescriptor(entry);
  const tone = severityTone(entry.severity);

  return (
    <li className="flex gap-2.5 px-3 py-2 transition-colors hover:bg-muted/45">
      {/* The tile column also draws the connector, so the line is always centred under the tile
          regardless of how tall the row's evidence makes it. */}
      <div className="flex shrink-0 flex-col items-center">
        {/* `rounded-[7px]`, 13px glyph, `leading-[1.55]` below: prototype-exact values (fidelity spec
            86d43a6v3 PART 3a). The shipped `rounded-md`/`h-3.5`/`leading-relaxed` were off by 1px/1px/.075
            on EVERY feed row — do not round these back to the scale. */}
        <span
          aria-hidden
          className={cn('flex h-6 w-6 items-center justify-center rounded-[7px]', tile)}
        >
          <Icon className="h-[13px] w-[13px]" />
        </span>
        {showConnector && <span aria-hidden className="mt-1 w-px flex-1 bg-border" />}
      </div>

      <div className="min-w-0 flex-1 space-y-1.5">
        <p className="text-[12.5px] leading-[1.55]">
          <span className="font-semibold text-foreground">{entry.actorSnapshot}</span>{' '}
          <span className="text-muted-foreground">{entry.summary}</span>
        </p>

        {/* Ordered DETAIL FIRST, REASON LAST by the server (§C4). Rendered in array order and never
            re-sorted — the reading order is "what happened, then why", and it is the server's to decide.
            Keyed by index because a block has no identity of its own; the array is rebuilt wholesale on
            every fetch, so there is nothing for a stable key to preserve. */}
        {entry.evidence.map((block, i) => (
          <EvidenceBlock key={i} evidence={block} tone={tone} />
        ))}

        <p className="text-[11px] tabular-nums text-muted-foreground">
          {/* The whole date, not the time alone (owner 2026-10-08): the day header scrolls away, and an entry
              is often read on its own. The hover keeps the seconds and the zone. */}
          <time dateTime={entry.occurredAtUtc} title={absolutePht(entry.occurredAtUtc)}>
            {phtFullDate(entry.occurredAtUtc)}, {phtTimeOfDay(entry.occurredAtUtc)}
          </time>
          {/* A null source renders NO suffix at all — not "Unknown", not a default. Every row written
              before E5 has one, and inventing an origin for them would be a claim the data does not make. */}
          {sourceLabel(entry.source) && (
            <span className="opacity-55"> · {sourceLabel(entry.source)}</span>
          )}
        </p>
      </div>
    </li>
  );
});

/**
 * `http:web` → `Web`, `http:mobile` → `Mobile`, `worker:PicklistPostingWorker` → `NetSuite integration`.
 *
 * The worker case is deliberately generalised rather than printed: a reader does not know what a
 * `PicklistPostingWorker` is, and the design's own example for a system row reads "NetSuite integration".
 * Anything unrecognised returns null and renders nothing, which is the honest answer for a token this
 * function has not been taught.
 */
function sourceLabel(source: string | null): string | null {
  if (!source) return null;
  if (source === 'http:web') return 'Web';
  if (source === 'http:mobile') return 'Mobile';
  if (source.startsWith('worker:')) return 'NetSuite integration';
  return null;
}

/** Severity → the tone a reason tag paints in. Only these three carry meaning; Info gets no colour. */
function severityTone(severity: ActivitySeverity): string | null {
  if (severity === ActivitySeverity.Critical) return 'destructive';
  if (severity === ActivitySeverity.Notable) return 'warning';
  return null;
}

/** PHT offset in ms. Fixed: the Philippines has observed no DST since 1978. See `lib/phtDay.ts`. */
const PHT_OFFSET_MS = 8 * 60 * 60 * 1000;

/**
 * The tooltip: the complete date, then the time WITH SECONDS — `Sep 29, 2026, 4:16:54 PM PHT`.
 *
 * The date is here (owner, 2026-10-05) because the row shows only a time and its day header is not always in
 * view.
 *
 * One action can now produce several rows in the same minute — a final pick emits both `PickRecorded` and
 * `PicklistFulfilled` in one transaction — and a burst produces many. Without seconds two rows an instant
 * apart carry identical tooltips, which makes the tooltip useless exactly when it is needed.
 */
function absolutePht(iso: string): string {
  const d = new Date(new Date(iso).getTime() + PHT_OFFSET_MS);
  const seconds = String(d.getUTCSeconds()).padStart(2, '0');
  return `${phtFullDate(iso)}, ${phtTimeOfDay(iso).replace(/(\d+:\d+)/, `$1:${seconds}`)} PHT`;
}
